import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Phone, ArrowRight, Check, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import FAQSection from "@/components/site/FAQSection";
import QuoteCTA from "@/components/site/QuoteCTA";
import { CITIES_BY_SLUG } from "@/lib/cities";
import { SERVICES, SITE } from "@/lib/site";

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES_BY_SLUG[slug];

  if (!city) return <Navigate to="/service-areas" replace />;

  const neighborCities = (city.neighbors || [])
    .map((s) => CITIES_BY_SLUG[s])
    .filter(Boolean);

  const cityTitle = `Parking Lot Striping & Sealcoating in ${city.city}, ${city.state}`;
  const cityDesc = `Professional parking lot striping, sealcoating, ADA compliance, and pavement marking in ${city.city}, ${city.stateName}. Free quotes from 901 Lots. Serving ${city.county}.`;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Parking Lot Striping and Sealcoating",
    provider: { "@type": "LocalBusiness", name: "901 Lots", "@id": "https://901lots.com/#business" },
    areaServed: {
      "@type": "City",
      name: city.city,
      containedInPlace: { "@type": "State", name: city.stateName },
    },
    description: cityDesc,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `Pavement services in ${city.city}, ${city.state}`,
      itemListElement: SERVICES.map((s, idx) => ({
        "@type": "Offer",
        position: idx + 1,
        itemOffered: { "@type": "Service", name: s.title, description: s.short },
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://901lots.com/" },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: "https://901lots.com/service-areas" },
      { "@type": "ListItem", position: 3, name: `${city.city}, ${city.state}`, item: `https://901lots.com/service-areas/${city.slug}` },
    ],
  };

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <SEO
        title={cityTitle}
        description={cityDesc}
        path={`/service-areas/${city.slug}`}
        jsonLd={[serviceSchema, breadcrumbSchema]}
      />

      {/* Hero */}
      <section className="relative bg-slate-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(/img/hero-parking-lot.jpg)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <nav className="text-xs text-slate-300 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/service-areas" className="hover:text-white">Service Areas</Link>
            <span>/</span>
            <span className="text-white">{city.city}, {city.state}</span>
          </nav>

          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/15 border border-orange-400/30 text-orange-300 text-xs font-bold tracking-[0.2em] uppercase">
            <MapPin className="h-3.5 w-3.5" />
            {city.county} · {city.drive}
          </div>

          <h1
            data-testid="city-h1"
            className="mt-4 font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl"
          >
            Parking lot striping &amp; sealcoating in{" "}
            <span className="text-orange-400">{city.city}, {city.state}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
            {city.intro}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button
              asChild
              size="lg"
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-7 py-6 rounded-md"
              data-testid="city-quote-cta"
            >
              <Link to="/quote">
                Get a Free {city.city} Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="bg-transparent text-white border-white/40 hover:bg-white hover:text-slate-900 font-semibold px-7 py-6 rounded-md"
            >
              <a href={SITE.phoneHref}>
                <Phone className="mr-2 h-5 w-5" /> Call {SITE.phone}
              </a>
            </Button>
          </div>
        </div>
        <div className="stripe-divider" aria-hidden="true" />
      </section>

      {/* Services in this city */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-blue-700">
              Local focus
            </div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-slate-900 text-3xl sm:text-4xl">
              What we typically do in {city.city}
            </h2>
            <ul className="mt-6 space-y-3">
              {city.services_focus.map((f) => (
                <li key={f} className="flex items-start gap-3 text-slate-700">
                  <Check className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {city.landmarks?.length > 0 && (
              <div className="mt-8 p-5 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
                  Areas we work near
                </div>
                <p className="mt-2 text-sm text-slate-700">
                  {city.landmarks.join(" · ")}
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to="/services"
                data-testid={`city-service-${s.slug}`}
                className="group bg-white border border-slate-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <h3 className="font-display font-semibold text-slate-900 text-lg">
                  {s.title} in {city.city}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.short}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:gap-2 transition-all">
                  Learn more <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why local */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="bg-white border border-slate-200 rounded-lg p-7">
            <Clock className="h-6 w-6 text-orange-600" />
            <h3 className="mt-4 font-display font-semibold text-slate-900 text-lg">
              After-hours in {city.city}
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              We schedule overnight and weekend crews so your {city.city} business stays open while we work.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-7">
            <MapPin className="h-6 w-6 text-orange-600" />
            <h3 className="mt-4 font-display font-semibold text-slate-900 text-lg">
              Local crew, local prices
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              No mileage charges, no out-of-town markups. We treat {city.city} like home — because it is.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-7">
            <Check className="h-6 w-6 text-orange-600" />
            <h3 className="mt-4 font-display font-semibold text-slate-900 text-lg">
              Fixed-price quotes
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              You'll know the total cost in writing before we ever lift a brush. No change-order surprises.
            </p>
          </div>
        </div>
      </section>

      {/* Neighbor cities (internal linking) */}
      {neighborCities.length > 0 && (
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display font-bold text-slate-900 text-2xl sm:text-3xl">
              Also serving near {city.city}
            </h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {neighborCities.map((n) => (
                <Link
                  key={n.slug}
                  to={`/service-areas/${n.slug}`}
                  data-testid={`neighbor-link-${n.slug}`}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-md text-sm font-medium text-slate-800 hover:border-blue-300 hover:bg-white transition-colors"
                >
                  <MapPin className="h-4 w-4 text-orange-600" />
                  {n.city}, {n.state}
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800"
              >
                See all 20 service areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ — visual only; schema emitted from / and /faq to avoid duplicates */}
      <FAQSection
        title={`Common questions from ${city.city} customers`}
        eyebrow="FAQ"
        subtitle="The questions we hear most often from property managers and business owners across the Mid-South."
        limit={6}
        showSeeAllLink
        background="slate"
      />

      <QuoteCTA />
    </div>
  );
}
